import React, { useState } from 'react';
import { Sankey, Tooltip, ResponsiveContainer } from 'recharts';
import ReportShell from './ReportShell';
import useReport from './useReport';
import toSankey, { sankeyHeight, sankeyNodePadding } from './charts/toSankey';
import RankedBars from './charts/RankedBars';

export default function CoachBehaviorReport() {
  // Coaches default to 'engaged', not 'organic' -- see Global Constraints.
  const [population, setPopulation] = useState('engaged');
  const { data, loading, error } = useReport('coach-behavior', population, '&days=30');

  return (
    <ReportShell
      title="Coach Behavior"
      headline={data?.headline}
      population={population}
      onPopulationChange={setPopulation}
      populationSize={data?.population_size ?? 0}
      populationNoun="coaches"
      thin={data?.thin}
      thinLabel="Too few sessions so far to read these as navigation patterns."
      loading={loading}
      error={error}
    >
      <h3>Where sessions end</h3>
      <p className="report-note">This is where coaches give up.</p>
      <RankedBars
        rows={(data?.exit_screens || [])
          // The backend's "(other)" residual is a bucket of everything past
          // its top-N cutoff and can carry the highest count of any row --
          // RankedBars sorts descending, so left in, it would draw FIRST,
          // directly under "This is where coaches give up." The table below
          // still carries it (kept last, where it reads as a residual).
          .filter((r) => r.screen !== '(other)')
          .map((r) => ({
            key: r.screen,
            label: r.screen,
            value: r.count,
            caption: `${r.count} (${Math.round(r.share * 100)}%)`,
          }))}
      />
      <div className="report-table-wrap">
        <table className="report-table">
          <thead><tr><th>Screen</th><th>Sessions ending here</th><th>Share</th></tr></thead>
          <tbody>
            {(data?.exit_screens || []).map((r) => (
              <tr key={r.screen}>
                <td>{r.screen}</td>
                <td>{r.count}</td>
                <td>{Math.round(r.share * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Most common moves</h3>
      {(() => {
        const sankey = toSankey(data?.transitions);
        return sankey.links.length > 0 ? (
          <>
            <div data-testid="coach-transitions-sankey" style={{ height: sankeyHeight(sankey.nodes.length, 260) }}>
              <ResponsiveContainer width="100%" height="100%">
                <Sankey data={sankey} nodePadding={sankeyNodePadding(sankey.nodes.length)} link={{ stroke: '#0066CC' }}>
                  <Tooltip />
                </Sankey>
              </ResponsiveContainer>
            </div>
            {sankey.droppedCount > 0 && (
              <p className="report-note">
                {sankey.droppedCount} looping move(s) omitted so the diagram can be drawn.
              </p>
            )}
          </>
        ) : (
          <p className="report-note">
            Not enough ordered moves yet to draw a flow.
          </p>
        );
      })()}
      <div className="report-table-wrap">
        <table className="report-table">
          <thead><tr><th>From</th><th>To</th><th>Times</th></tr></thead>
          <tbody>
            {(data?.transitions || []).map((r) => (
              <tr key={`${r.from_screen}->${r.to_screen}`}>
                <td>{r.from_screen}</td><td>{r.to_screen}</td><td>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>Coaches</h3>
      <p className="report-note">
        “Screen views” is de-duplicated the same way the headline above is —
        repeat views of the same screen within 30 minutes count once — so
        this column sums to the headline’s screen-view total.
      </p>
      <div className="report-table-wrap">
        <table className="report-table">
          <thead><tr><th>Coach</th><th>Sessions</th><th>Screen views (de-duped)</th><th>Last active</th></tr></thead>
          <tbody>
            {(data?.coaches || []).map((c) => (
              <tr key={c.user_id}>
                <td>{c.name}</td><td>{c.sessions}</td><td>{c.screens}</td>
                <td>{c.last_active ? new Date(c.last_active).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3>What this report cannot see</h3>
      <ul className="report-gaps">
        {(data?.coverage_gaps || []).map((g) => <li key={g}>{g}</li>)}
      </ul>
    </ReportShell>
  );
}
